// The pests that turn up for a companion to deal with. Three kinds of one shared thing, so every
// companion's attack (abilities/) works on each of them unchanged:
//
//   bug        the common one, for everyone: a little beetle (a ladybug, a jewel beetle, now and
//              then a golden scarab) that crawls out from behind the edge it's on (a window's top,
//              the floor's edge) or out of the end of a line of text. It scuttles and wiggles its
//              antennae.
//   glitch     the rare one: a "missing texture" cube in a vibrant color scheme of its own each time
//              (magenta and black, cyan and black, lime and purple, rainbow noise...). It loads in
//              out of static, jitters, skips sideways, and shatters into pixels when it's hit.
//   endermite  only out of an ender pearl (travel.js), so only for those that use pearls.
//
// A pest is a prop (stage.js draws it with its draw(now)) with what the attacks use: x, y (where
// it stands), s (its pixel size), ledge and dx (the surface it's on; an attack sets ledge to null
// to throw it about), face (-1 left, 1 right), alpha, hit (1 while it's hit: it flashes), alive,
// its type ('bug', 'glitch' or 'endermite') and colors (its own, for bits that fly off it). The
// attacks call it `mite` and move and finish it with miteTo and killMite, names from when the
// endermite was the only one.

import { clockNow } from './clock.js';
import { env, rand, lerp, hash, wait, animate, weighted } from './core.js';
import { blit, mirror, sprite } from './pixels.js';
import { mixHex, blendHex } from './color.js';
import { props } from './stage.js';
import { baseOf } from './travel.js';
import * as fx from './particles.js';

const ENDER = ['#B05CE0', '#7A2BD9', '#E0B3FF', '#5A1AA8'];
const POOF = ['#F2F2F2', '#CFCFCF', '#A9A9A9'];   // the grey poof a Minecraft mob goes in
const HURT = () => '#FF6B6B';                     // the endermite's red flash while it's hit
const BUG_HURT = col => blendHex(col, '#FF3030', 0.55);   // a bug's, red but still itself

// A drawing whose letters stay letters (tones), colored when it's drawn.
const tones = letters => Object.fromEntries([...letters].map(ch => [ch, ch]));

// ---------- the endermite (facing left; mirrored to face right) ----------
export function endermitePixels(now, facing) {
  const step = Math.floor(now / 110) % 2;
  const px = [
    [2, -3, '#6E5C82'], [3, -3, '#6E5C82'], [4, -3, '#6E5C82'], [5, -3, '#6E5C82'],
    [0, -2, '#C060F0'], [1, -2, '#4E3F5E'], [2, -2, '#4E3F5E'], [3, -2, '#5B4A6B'], [4, -2, '#4E3F5E'], [5, -2, '#5B4A6B'], [6, -2, '#6E5C82'],
  ];
  for (const c of step ? [1, 3, 5] : [2, 4, 6]) px.push([c, -1, '#3A2F47']);
  const shifted = px.map(([c, r, t]) => [c - 3, r, t]);
  return facing > 0 ? shifted.map(([c, r, t]) => [-1 - c, r, t]) : shifted;
}

// ---------- the bug ----------
// A little beetle seen from the side, facing left, its feet on row 0: a domed shell (S, its light
// top L, a shine W, spots P, the dark underside D) and its head K with an eye E. The antenna (A)
// and the legs (k) are added per frame.
const BUG = sprite(`
...LLL..
..KSWSP.
.KEPSSSS
.KKDDDDD
`, tones('LKSWPDE'), -4, -5);
const BUG_ROWS = 6;                                   // antenna to feet
const ANTENNA = [[[-3, -5], [-4, -6]], [[-3, -5], [-4, -5]], [[-3, -5], [-3, -6]]];   // ahead and up, forward, straight up
const LEGS = [[-2, 0, 2], [-1, 1, 3]];
// Its looks, and how often each turns up: a ladybug, an iridescent jewel beetle (its shell shifts
// from green to blue in a wave), and now and then a golden scarab with a glint and a twinkle.
export const BUG_LOOKS = {
  ladybug: { label: 'Ladybug', w: 5, S: '#E43B30', L: '#FF7A6B', W: '#FFFFFF', P: '#1C1515', D: '#A3221C', K: '#2A2020', E: '#FFFFFF' },
  jewel: { label: 'Jewel beetle', w: 4, S: '#22B573', L: '#7BF0B4', W: '#F0FFF6', P: '#22B573', D: '#0F6B47', K: '#1D2E40', E: '#F0FFF6',
    shimmer: { S: '#1E86D6', L: '#86D8FF', D: '#0D4A7A' } },
  scarab: { label: 'Golden scarab', w: 1, S: '#F2B630', L: '#FFE07A', W: '#FFF8DC', P: '#F2B630', D: '#B07A12', K: '#5A3A12', E: '#FFF6D0', glint: true },
};
const GLINT = [[-1, -5], [0, -5], [1, -5], [2, -4], [3, -3]];   // the glint's way over a scarab's back

function bugPixels(p, now) {
  const look = BUG_LOOKS[p.look];
  // Legs step only while it moves (it has moved since it was last drawn); its antenna twitches
  // now and then.
  if (p.lastX !== undefined && Math.abs(p.x - p.lastX) > 0.05) p.movedAt = now;
  p.lastX = p.x;
  const walking = now - (p.movedAt ?? -Infinity) < 120;
  const tw = hash(p.seed, Math.floor(now / 170), 3), antenna = walking ? 1 : tw < 0.62 ? 0 : tw < 0.82 ? 1 : 2;
  const glint = look.glint ? Math.floor(now / 80) % 22 : -1;
  const px = BUG.map(([c, r, t]) => {
    let col = look[t];
    if (look.shimmer && 'SLD'.includes(t)) col = blendHex(col, look.shimmer[t], 0.5 + 0.5 * Math.sin(now / 450 - c * 0.7));
    if (GLINT[glint] && GLINT[glint][0] === c && GLINT[glint][1] === r) col = '#FFFFFF';
    return [c, r, col];
  });
  for (const [c, r] of ANTENNA[antenna]) px.push([c, r, look.K]);
  for (const c of LEGS[walking ? Math.floor(now / 70) % 2 : 0]) px.push([c, -1, look.K]);
  if (glint >= 7 && glint <= 9 && p.rise === undefined) {   // a twinkle over its back
    px.push([1, -7, '#FFFFFF']);
    if (glint === 8) px.push([0, -7, '#FFF3B0'], [2, -7, '#FFF3B0'], [1, -8, '#FFF3B0'], [1, -6, '#FFF3B0']);
  }
  const turned = p.face > 0 ? mirror(px) : px;
  // Coming out from behind its edge: only what's above the edge shows.
  if (p.rise === undefined) return turned;
  const down = BUG_ROWS - p.rise;
  return turned.filter(([, r]) => r + down < 0).map(([c, r, t]) => [c, r + down, t]);
}

// ---------- the glitch ----------
// A cube with a missing texture: a 4 x 4 front in quarters of its two colors (a and b), its top
// (A, B: lighter) above and its side (x, y: darker) to the right, its feet on row 0.
const CUBE = sprite(`
.BBAA
aabby
aabby
bbaax
bbaa.
`, tones('aAbBxy'), -2, -5);
export const GLITCH_SCHEMES = {
  classic: { label: 'Missing texture', w: 3, a: '#F800F8', b: '#000000' },
  cyan: { label: 'Cyan', w: 1, a: '#00F0FF', b: '#000000' },
  lime: { label: 'Lime and purple', w: 1, a: '#A8FF00', b: '#7A00FF' },
  sunset: { label: 'Orange and blue', w: 1, a: '#FF6A00', b: '#0047FF' },
  candy: { label: 'Yellow and pink', w: 1, a: '#FFE600', b: '#FF0090' },
  rainbow: { label: 'Rainbow noise', w: 1, a: null, b: '#000000' },   // a: every pixel its own hue, flickering
};

// The color of one of its pixels. Hit, its two colors swap.
function cubeColor(p, t, c, r, now) {
  const sc = GLITCH_SCHEMES[p.look], first = 'aAx'.includes(t) !== (p.hit > 0);
  const shade = t === 'A' || t === 'B' ? 'light' : t === 'x' || t === 'y' ? 'dark' : null;
  if (first && !sc.a) {                             // rainbow noise
    const hue = Math.floor(hash(p.seed + c * 31, r, Math.floor(now / 90)) * 360);
    return `hsl(${hue} 100% ${shade === 'light' ? 76 : shade === 'dark' ? 34 : 56}%)`;
  }
  const col = first ? sc.a : sc.b;
  return shade === 'light' ? mixHex(col, 1.45) : shade === 'dark' ? mixHex(col, 0.6) : col;
}
const mainColor = (p, now) => cubeColor(p, 'a', 0, 0, now);

// The cube, turned to the side it faces.
const cubePixels = p => (p.face < 0 ? mirror(CUBE) : CUBE);

// Drawn pixel by pixel, glitching in bursts with calm in between: a jitter, a skip a few pixels
// sideways with a ghost left behind, a row torn sideways, a stray pixel, a flicker; and a bright
// scanline runs down it now and then. All from its seed and the time, so a moment always looks
// the same.
function drawGlitch(p, now) {
  const ctx = env.ctx, s = p.s, k = Math.floor(now / 70), h = n => hash(p.seed, k, n);
  const X = Math.round(p.x), Y = Math.round(p.y), alpha = ctx.globalAlpha;
  if (p.off) return switchOff(p, now, X, Y);
  const load = p.load ?? 1, wild = p.hit > 0 || hash(p.seed, Math.floor(now / 450), 13) < 0.3;
  const skip = wild && h(3) < 0.15 ? (h(4) < 0.5 ? -3 : 3) : 0;
  const ox = skip || (h(1) < (wild ? 0.3 : 0.04) ? (h(2) < 0.5 ? -1 : 1) : 0);
  const tearRow = p.hit || (wild && h(5) < 0.35) ? -1 - Math.floor(h(6) * 5) : 0, tear = (h(7) < 0.5 ? -1 : 1) * (p.hit ? 2 : 1);
  const scan = -5 + (Math.floor(now / 55) % 36);
  // Where it was a moment ago, after a skip or a jump along the ground: a fading ghost.
  if (p.lastX !== undefined && Math.abs(p.x - p.lastX) >= 2 * s) p.trail = { x: p.lastX, y: p.y, at: now };
  p.lastX = p.x;
  const ghosts = [];
  if (skip && load === 1) ghosts.push({ x: X, y: Y, a: 0.3 });
  if (p.trail && now - p.trail.at < 140) ghosts.push({ x: Math.round(p.trail.x), y: Math.round(p.trail.y), a: 0.4 * (1 - (now - p.trail.at) / 140) });
  for (const gh of ghosts) {
    ctx.globalAlpha = alpha * gh.a;
    ctx.fillStyle = GLITCH_SCHEMES[p.look].a || '#FFFFFF';
    for (const [c, r] of cubePixels(p)) ctx.fillRect(gh.x + c * s, gh.y + r * s, s, s);
  }
  ctx.globalAlpha = alpha * (load === 1 && wild && h(11) < 0.12 ? 0.45 : 1);   // a flicker
  for (const [c, r, t] of cubePixels(p)) {
    let dx = ox + (r === tearRow ? tear : 0);
    if (load < 1) {                                  // loading in: a scatter of its pixels, rows jumbled
      if (hash(p.seed + c * 7, r + 9, k) > load) continue;
      dx += Math.round((hash(p.seed, r, k) - 0.5) * 6 * (1 - load));
    }
    const col = cubeColor(p, t, c, r, now);
    ctx.fillStyle = r === scan && col[0] === '#' ? blendHex(col, '#FFFFFF', 0.55) : col;
    ctx.fillRect(X + (c + dx) * s, Y + r * s, s, s);
  }
  if (load === 1 && h(8) < (wild ? 0.3 : 0.03)) {   // a stray pixel next to it
    const c = -5 + Math.floor(h(9) * 11), r = -8 + Math.floor(h(10) * 8);
    if (c < -2 || c > 2 || r < -5) { ctx.fillStyle = h(12) < 0.5 ? mainColor(p, now) : '#FFFFFF'; ctx.fillRect(X + c * s, Y + r * s, s, s); }
  }
  ctx.globalAlpha = alpha;
}

// Gone like an old screen switching off: it squashes into a bright line, which shrinks to a dot.
function switchOff(p, now, X, Y) {
  const ctx = env.ctx, s = p.s, t = p.off;
  if (t < 0.45) {
    const k = t / 0.45, rows = Math.max(1, Math.round(5 * (1 - k))), top = -3 - Math.floor((rows - 1) / 2), cube = cubePixels(p);
    for (let j = 0; j < rows; j++) {
      const from = -5 + Math.floor((j * 5) / rows);  // the cube's row shown there
      for (const [c, r, tone] of cube) {
        if (r !== from) continue;
        const col = cubeColor(p, tone, c, r, now);
        ctx.fillStyle = col[0] === '#' ? blendHex(col, '#FFFFFF', k) : col;
        ctx.fillRect(X + c * s, Y + (top + j) * s, s, s);
      }
    }
  } else if (t < 1) {
    const w = Math.max(1, Math.round(7 * (1 - (t - 0.45) / 0.55)));
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(X - Math.floor(w / 2) * s, Y - 3 * s, w * s, s);
  }
}

// ---------- what each kind does ----------
// hide: how it waits to turn up. appear: how it turns up, next to the companion. walk: where it
// is t of the way along a scuttle (miteTo). die: how it goes when it's beaten. leave: how it goes
// when nobody deals with it.
const KINDS = {
  endermite: {
    colors: () => ENDER,
    draw: (p, now) => blit(endermitePixels(now, p.face), p.x, p.y, p.s, now, null, p.hit ? HURT : null),
    hide: p => { p.alpha = 0; },
    async appear(g, p) {
      fx.burst(p.x, p.y - 2 * p.s, p.s, ENDER, 10, 5, -8, 500);
      await animate(220, g, t => { p.alpha = t; });
    },
    walk: (p, d0, d1, t) => lerp(d0, d1, t) + Math.sin(t * 22) * 0.5 * p.s,
    die: p => fx.burst(p.x, p.y - 2 * p.s, p.s, POOF, 12, 4, -30, 700),
    async leave(g, p) {                              // it burrows away
      await animate(400, g, t => { p.alpha = 1 - t; });
      fx.puff(p.x, p.y - 2 * p.s, p.s, '#7A6A5A');
    },
  },
  bug: {
    colors: p => { const l = BUG_LOOKS[p.look]; return [l.S, l.D, l.L, l.K]; },
    draw: (p, now) => blit(bugPixels(p, now), p.x, p.y, p.s, now, null, p.hit ? BUG_HURT : null),
    hide: p => { p.rise = 0; },                      // rows of it that show above its edge
    // Up from behind the edge: its antenna and the top of its shell peek over and look around,
    // then it climbs out and shakes off a little dust.
    async appear(g, p) {
      await animate(300, g, t => { p.rise = Math.round(2 * t); });
      await wait(650, g);
      await animate(320, g, t => { p.rise = 2 + Math.round(4 * t); });
      delete p.rise;
      fx.burst(p.x, p.y - p.s, p.s, ['#E9E2D8', '#CFC6BA'], 5, 5, 10, 320);
    },
    // Scuttling in little dashes, a stop between each.
    walk: (p, d0, d1, t) => {
      const n = Math.max(1, Math.round(Math.abs(d1 - d0) / (9 * p.s)));
      return lerp(d0, d1, t - Math.sin(2 * Math.PI * n * t) / (2 * Math.PI * n));
    },
    // Beaten: it flips onto its back with a pop, legs in the air, and fades.
    die: p => {
      const s = p.s, flipped = bugPixels(p, clockNow()).filter(([, r]) => r >= -BUG_ROWS).map(([c, r, t]) => [c, -BUG_ROWS - 1 - r, t]);
      fx.burst(p.x, p.y - 2 * s, s, POOF, 10, 6, -30, 600);
      fx.emit({ kind: 'sprite', art: flipped, x: p.x, y: p.y, vx: -p.face * 3 * s, vy: -22 * s, g: 70 * s, life: 700, scale: s });
    },
    async leave(g, p) {                              // back behind the edge
      await animate(450, g, t => { p.rise = Math.round(BUG_ROWS * (1 - t)); });
    },
  },
  glitch: {
    colors: p => { const a = mainColor(p, 0), b = GLITCH_SCHEMES[p.look].b; return [a, b, a[0] === '#' ? mixHex(a, 1.45) : '#FFFFFF', '#FFFFFF']; },
    draw: drawGlitch,
    hide: p => { p.load = 0; },                      // how much of it has loaded in
    async appear(g, p) {                             // it loads in out of static
      await animate(700, g, t => { p.load = t * t; });
      delete p.load;
    },
    // It doesn't walk: it skips along, a few pixels at a time.
    walk: (p, d0, d1, t) => {
      const n = Math.max(1, Math.round(Math.abs(d1 - d0) / (3 * p.s)));
      return lerp(d0, d1, Math.floor(t * n + 0.5) / n);
    },
    // Shattered: every one of its pixels flies off on its own, falling, with a white flash.
    die: p => {
      const s = p.s, now = clockNow();
      for (const [c, r, t] of cubePixels(p)) {
        fx.emit({ kind: 'square', x: p.x + (c + 0.5) * s, y: p.y + (r + 0.5) * s, vx: (c + rand(-1, 1)) * 9 * s, vy: ((r + 3) * 6 - rand(14, 24)) * s,
          g: 70 * s, life: rand(450, 800), size: s / env.S, color: cubeColor(p, t, c, r, now), fade: 1 });
      }
      fx.burst(p.x, p.y - 2.5 * s, s, ['#FFFFFF', '#FFFFFF', KINDS.glitch.colors(p)[0]], 6, 12, 0, 260);
    },
    async leave(g, p) { await animate(380, g, t => { p.off = t; }); },
  },
};
// A pest of a kind, not yet anywhere. Its look (a bug's, a glitch's color scheme) and the seed
// its twitches and glitches follow are picked at random unless given.
export function makePest(type, { owner = null, ledge = null, dx = 0, x = 0, y = 0, s = env.S, face = 1, look, seed } = {}) {
  const looks = type === 'bug' ? BUG_LOOKS : type === 'glitch' ? GLITCH_SCHEMES : null;
  const p = { kind: 'pest', type, owner, ledge, dx, x, y, s, face, alpha: 1, hit: 0, alive: true, draw: now => KINDS[type].draw(p, now) };
  if (looks) {
    p.look = look || weighted(Object.keys(looks), k => looks[k].w);
    p.seed = seed ?? Math.floor(Math.random() * 100000);
  }
  p.colors = KINDS[type].colors(p);
  return p;
}

// Puts a pest of a kind on the companion's surface, beside it, still hidden (pestAppears brings
// it out). A bug on a line of text comes out of the end of the text when there's room. Null if
// there's no room.
export function spawnPest(g, spot, type) {
  const me = g.me, s = me.s, base = baseOf(spot), geo = base.geom();
  if (!geo) return null;
  if (type === 'bug' && base.kind === 'text' && me.x - geo.x1 >= 18 * s) {
    return place(makePest(type, { owner: me, ledge: base, dx: 4 * s, x: geo.x1 + 4 * s, y: geo.y, s, face: 1 }));
  }
  const side = me.x + 26 * s < geo.x2 - 4 * s ? 1 : me.x - 26 * s > geo.x1 + 4 * s ? -1 : 0;
  if (!side) return null;
  const dx = me.x + side * 24 * s - geo.x1;
  return place(makePest(type, { owner: me, ledge: base, dx, x: geo.x1 + dx, y: geo.y, s, face: -side }));
}
function place(p) {
  KINDS[p.type].hide(p);
  props.add(p);
  return p;
}

// How it turns up (the endermite in a purple burst, the bug over the edge, the glitch out of
// static), and how it goes when nobody deals with it.
export const pestAppears = (g, p) => KINDS[p.type].appear(g, p);
export async function pestLeaves(g, p) {
  await KINDS[p.type].leave(g, p);
  p.alive = false;
  props.delete(p);
}

// Something else can stand in for a pest by bringing its own die and walk (in a duel, the other
// companion: fights.js).
export function killMite(mite) {
  if (!mite.alive) return;
  mite.alive = false;
  (mite.die || KINDS[mite.type].die)(mite);
  props.delete(mite);
}

// It scuttles (skips, for a glitch) along its surface to x over ms.
export async function miteTo(g, mite, x, ms) {
  const geo = mite.ledge?.geom();
  if (!geo || !mite.alive) return;
  const d0 = mite.dx, d1 = x - geo.x1, walk = mite.walk || KINDS[mite.type].walk;
  mite.face = Math.sign(d1 - d0) || mite.face;
  await animate(ms, g, t => { mite.dx = walk(mite, d0, d1, t); });
}
